import React, { useState, useEffect, useCallback } from "react";
import WavesurferPlayer from "@wavesurfer/react";
import { formatAudioTime } from "../../../../utils/dateUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlay,
  faPause,
  faVolumeMute,
  faVolumeUp,
  faPlus,
  faMinus,
  faGaugeHigh,
  faRedo,
  faLeftRight,
} from "@fortawesome/free-solid-svg-icons";
import "./AudioPlayer.css";
import InfoTooltip from "../../../InfoTooltip";
import TipButton from "../../../TipButton";
import StudentInfoTooltip from "../../../StudentInfoTooltip";

const SPEED_OPTIONS = [0.5, 0.75, 1, 1.25, 1.5, 2];

const AudioPlayer = ({
  audioURL,
  onRetry,
  question,
  onChange,
  isStudentView,
  tip,
  border,
  onFlip,
}) => {
  const [wavesurfer, setWavesurfer] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const [volume, setVolume] = useState(50);
  const [isMuted, setIsMuted] = useState(false);
  const [speed, setSpeed] = useState(1);

  useEffect(() => {
    if (wavesurfer) wavesurfer.setPlaybackRate(speed);
  }, [wavesurfer, speed]);

  const onWavesurferReady = (wavesurfer) => {
    setWavesurfer(wavesurfer);
    setDuration(wavesurfer.getDuration());
  };

  const onWavesurferAudioProcess = useCallback(() => {
    if (wavesurfer) setCurrentTime(wavesurfer.getCurrentTime());
  }, [wavesurfer]);

  const onWavesurferSeek = useCallback(() => {
    if (wavesurfer) setCurrentTime(wavesurfer.getCurrentTime());
  }, [wavesurfer]);

  const onPlayPause = useCallback(() => {
    setIsPlaying((playing) => {
      if (wavesurfer?.isPlaying() !== !playing) {
        wavesurfer?.playPause();
        return !playing;
      }
      return playing;
    });
  }, [wavesurfer]);

  const handleVolumeChange = useCallback(
    (event) => {
      const newVolume = event.target.value;
      setVolume(newVolume);
      wavesurfer?.setVolume(newVolume / 100);
    },
    [wavesurfer]
  );

  const toggleMute = useCallback(() => {
    if (wavesurfer) {
      const newMuteState = !isMuted;
      wavesurfer.setVolume(newMuteState ? 0 : volume / 100);
      setIsMuted(newMuteState);
    }
  }, [wavesurfer, isMuted, volume]);

  const changeSpeed = useCallback((delta) => {
    setSpeed((prevSpeed) => {
      const currentIndex = SPEED_OPTIONS.indexOf(prevSpeed);
      const newIndex = Math.max(
        0,
        Math.min(SPEED_OPTIONS.length - 1, currentIndex + delta)
      );
      return SPEED_OPTIONS[newIndex];
    });
  }, []);

  return (
    <div className={`question-container ${border}`}>
      <div className="question-header">
        {isStudentView ? (
          <>
            {question ? (
              <div className="audio-recorder-question">{question}</div>
            ) : (
              <div className="audio-recorder-question">
                Please listen to this clip by pressing the play button.
              </div>
            )}
            <div className="header-spacer"></div>
            <StudentInfoTooltip type="Audio Player" />
          </>
        ) : (
          <>
            <input
              type="text"
              value={question}
              onChange={(e) => onChange({ question: e.target.value })}
              placeholder="Tell your students what this audioclip is about (optional)"
              className="question-input"
            />
            <InfoTooltip type="Audio Player" />
          </>
        )}
      </div>
      <div className="player">
        <button onClick={onPlayPause} className="icon-button play-pause-button">
          <FontAwesomeIcon
            icon={isPlaying ? faPause : faPlay}
            color="#97c098"
            size="lg"
          />
        </button>
        <div className="wavesurfer-container">
          <WavesurferPlayer
            height={50}
            barHeight={1}
            barWidth={2}
            waveColor="black"
            progressColor="#a9a9a9"
            url={audioURL}
            onReady={onWavesurferReady}
            onAudioprocess={onWavesurferAudioProcess}
            onSeeking={onWavesurferSeek}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
          />
        </div>
      </div>

      <div className="player-controls">
        <VolumeControl
          volume={volume}
          isMuted={isMuted}
          onVolumeChange={handleVolumeChange}
          onToggleMute={toggleMute}
        />
        <SpeedControl
          speed={speed}
          onIncreaseSpeed={() => changeSpeed(1)}
          onDecreaseSpeed={() => changeSpeed(-1)}
        />
        <TipButton
          tip={tip}
          onChange={onChange}
          isStudentView={isStudentView}
          onFlip={onFlip}
        />
        <div className="right-controls">
          <button onClick={onRetry} className="icon-button retry-button">
            <FontAwesomeIcon icon={faRedo} color="414141" size="sm" />
          </button>
          <div className="time-display">
            {formatAudioTime(currentTime)} / {formatAudioTime(duration)}
          </div>
        </div>
      </div>
    </div>
  );
};

const VolumeControl = ({ volume, isMuted, onVolumeChange, onToggleMute }) => (
  <div className="volume-control">
    <button onClick={onToggleMute} className="icon-button">
      <FontAwesomeIcon
        icon={isMuted ? faVolumeMute : faVolumeUp}
        color="black"
        size="sm"
      />
    </button>
    <input
      type="range"
      className="volume-slider"
      min="0"
      max="100"
      value={volume}
      onChange={onVolumeChange}
      title="Volume"
      disabled={isMuted}
    />
  </div>
);

const SpeedControl = ({ speed, onDecreaseSpeed, onIncreaseSpeed }) => (
  <div className="speed-control">
    <FontAwesomeIcon icon={faGaugeHigh} color="414141" size="lg" />
    <button onClick={onDecreaseSpeed} className="icon-button">
      <FontAwesomeIcon icon={faMinus} color="414141" size="sm" />
    </button>
    <span className="speed-display">{speed}x</span>
    <button onClick={onIncreaseSpeed} className="icon-button">
      <FontAwesomeIcon icon={faPlus} color="414141" size="sm" />
    </button>
  </div>
);

export default AudioPlayer;
